import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getProgramMembers,
  getProgramFollowers,
  getProgramApplicants,
  followProgram,
  unfollowProgram,
  getFollowingStatus,
  waitlistProgram,
  applyToProgram,
  approveApplication,
  declineApplication,
  setMemberRole,
  setMemberStatus,
  removeMember,
  notify,
} from '../api/programs';

export function useProgramMembers(programId, initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef();

  const fetchMembers = useCallback(async () => {
    if (!programId) return;
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await getProgramMembers(programId, filters, { signal: controller.signal });
      setData(Array.isArray(response) ? response : []);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err);
    } finally {
      setLoading(false);
    }
  }, [programId, filters]);

  useEffect(() => {
    fetchMembers();
    return () => abortRef.current?.abort?.();
  }, [fetchMembers]);

  return { data, loading, error, refetch: fetchMembers, filters, setFilters };
}

export function useProgramFollowers(programId, enabled = true, params = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!programId || !enabled) return;
      setLoading(true);
      try {
        const res = await getProgramFollowers(programId, params);
        
        // Handle both old and new API formats
        let list = [];
        if (Array.isArray(res?.followers)) {
          // New format: { followers: [...] }
          list = res.followers;
        } else if (Array.isArray(res)) {
          // Old format: [{ status: 'Following', user: {...} }]
          list = res.filter(item => item.status === 'Following');
        }
        if (mounted) setData(list);
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [programId, enabled, JSON.stringify(params), refreshKey]);
  return { data, loading, error, refetch };
}

export function useProgramApplicants(programId, enabled = true, params = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!programId || !enabled) return;
      setLoading(true);
      try {
        const res = await getProgramApplicants(programId, params);
        // applicants endpoint returns { success, applicants, pagination }
        const list = Array.isArray(res?.applicants) ? res.applicants : (Array.isArray(res) ? res : []);
        if (mounted) setData(list);
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [programId, enabled, JSON.stringify(params), refreshKey]);
  return { data, loading, error, refetch };
}

// Mutations
export function useProgramMutations(programId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const wrap = useCallback(async (fn) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      return result;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    follow: (notes) => wrap(() => followProgram(programId, { notes })),
    unfollow: () => wrap(() => unfollowProgram(programId)),
    getFollowingStatus: () => wrap(() => getFollowingStatus(programId)),
    waitlist: (notes) => wrap(() => waitlistProgram(programId, { notes })),
    apply: (payload) => wrap(() => applyToProgram(programId, payload)),
    approve: (applicationId, payload) => wrap(() => approveApplication(programId, applicationId, payload)),
    decline: (applicationId, payload) => wrap(() => declineApplication(programId, applicationId, payload)),
    setRole: (userId, role, notes) => wrap(() => setMemberRole(programId, userId, role, notes)),
    setStatus: (userId, status, notes) => wrap(() => setMemberStatus(programId, userId, status, notes)),
    remove: (userId, notes) => wrap(() => removeMember(programId, userId, notes)),
    notify: (payload) => wrap(() => notify(programId, payload)),
  };
}

// Permission helpers
export const canManageProgram = (perms = []) =>
  Array.isArray(perms) && (perms.includes('program.manage') || perms.includes('club.manage'));

export const canAssignStaff = (perms = []) => {
  const hasPermission = Array.isArray(perms) && (perms.includes('program.manage') || perms.includes('program.staff.manage') || perms.includes('club.manage'));
  
  // Debug logging (can be removed in production)
  // console.log('🔐 canAssignStaff Check:', { permissions: perms, result: hasPermission });
  
  return hasPermission;
};

// Status transitions
const transitions = {
  Following: ['Applied', 'Waitlisted'],
  Waitlisted: ['Applied', 'Enrolled'], // Can apply or be directly enrolled
  Applied: ['Enrolled', 'Declined'],
  Enrolled: ['Suspended', 'Withdrawn', 'Completed'],
  Suspended: ['Enrolled', 'Withdrawn'], // Can restore to Enrolled or escalate to Withdrawn
  Withdrawn: ['Enrolled'], // Can restore to Enrolled (reinstatement)
};

export const getAllowedStatusTransitions = (currentStatus) => transitions[currentStatus] || [];


